import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Settings|Score Report Batch|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamScoreReportsBatch,
	portal: 'SP',
};

export type SPSettingsScoreReportsBatchLocNames =
	| 'SPSettingsScoreReportsBatchTitle'
	| 'SPSettingsScoreReportsBatchHeading'
	| 'SPSettingsScoreReportsBatchColumnTestDate'
	| 'SPSettingsScoreReportsBatchColumnFirstName'
	| 'SPSettingsScoreReportsBatchColumnLastName'
	| 'SPSettingsScoreReportsBatchColumnDivision'
	| 'SPSettingsScoreReportsBatchColumnRegistrationNumber'
	| 'SPSettingsScoreReportsBatchFilterByExam'
	| 'SPSettingsScoreReportsBatchFilterByResult'
	| 'SPSettingsScoreReportsBatchFilterByExamAllOption'
	| 'SPSettingsScoreReportsBatchFilterByResultAllOption'
	| 'SPSettingsScoreReportsBatchFilterByJurisdiction'
	| 'SPSettingScoreReportsBatchTooManySelected'
	| 'SPSettingScoreReportsBatchNoneSelected'
	| 'SPSettingScoreReportsBatchEmptyData'
	| 'SPSettingsScoreReportsBatchLabelExamResult'
	| 'SPSettingsScoreReportsBatchErrorJurisdictionRequired'
	| 'SPSettingsScoreReportsBatchErrorGradeRequired'
	| 'SPSettingsScoreReportsBatchErrorExamRequired'
	| 'SPSettingsScoreReportsBatchButtonFindScoreReports';

export const spSettingsScoreReportsBatchLocs: LocDefs<SPSettingsScoreReportsBatchLocNames> = {
	SPSettingsScoreReportsBatchTitle: {
		defaultValue: 'Score Report Batch',
		description: 'Title for the score report batch page.',
		displayPath: `${pathStart}Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPSettingScoreReportsBatchTooManySelected: {
		defaultValue: 'Unable to batch more than 300 score reports at a time.',
		description: 'Error message for too many score reports selected.',
		displayPath: `${pathStart}Too Many Selected`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSettingScoreReportsBatchNoneSelected: {
		defaultValue: 'No score reports selected.',
		description: 'Error message for no score reports selected.',
		displayPath: `${pathStart}None Selected`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSettingScoreReportsBatchEmptyData: {
		defaultValue: 'No data found.',
		description: 'Message displayed when a table is empty.',
		displayPath: `${pathStart}Empty Data`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchHeading: {
		defaultValue: 'Score Report Batch',
		description: 'Heading for the Score report batch page',
		displayPath: `${pathStart}Heading`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchColumnTestDate: {
		defaultValue: 'Test Date',
		description: 'Column heading for the test date',
		displayPath: `${pathStart}Column Heading | TestDate`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchColumnFirstName: {
		defaultValue: 'First Name',
		description: 'Column heading for the first name',
		displayPath: `${pathStart}Column Heading | First Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchColumnLastName: {
		defaultValue: 'Last Name',
		description: 'Column heading for the last name',
		displayPath: `${pathStart}Column Heading | Last Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchColumnDivision: {
		defaultValue: 'Division',
		description: 'Column heading for the division',
		displayPath: `${pathStart}Column Heading | Division`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchColumnRegistrationNumber: {
		defaultValue: 'Registration Number',
		description: 'Column heading for the registration number',
		displayPath: `${pathStart}Column Heading | Registration Number`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchFilterByExam: {
		defaultValue: 'Exam',
		description: 'Filter by exam',
		displayPath: `${pathStart}Filter | Exam`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchFilterByResult: {
		defaultValue: 'Result',
		description: 'Filter by result',
		displayPath: `${pathStart}Filter | Result`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchFilterByExamAllOption: {
		defaultValue: 'All Exams',
		description: 'First option for the exam filter that selects all exams.',
		displayPath: `${pathStart}Filter | Exam All Options`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchFilterByResultAllOption: {
		defaultValue: 'Pass and Fail',
		description: 'First option for the result filter that selects pass and fail.',
		displayPath: `${pathStart}Filter | Result All Options`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchFilterByJurisdiction: {
		defaultValue: 'Jurisdiction',
		description: 'Filter by jurisdiction',
		displayPath: `${pathStart}Filter | Jurisdiction`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchLabelExamResult: {
		defaultValue: 'Exam Result',
		description: 'Label for the exam result',
		displayPath: `${pathStart}Label | Exam Result`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchErrorJurisdictionRequired: {
		defaultValue: 'Jurisdiction is required',
		description: 'Error message for jurisdiction required',
		displayPath: `${pathStart}Error | Jurisdiction Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchErrorGradeRequired: {
		defaultValue: 'Grade is required',
		description: 'Error message for grade required',
		displayPath: `${pathStart}Error | Grade Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchErrorExamRequired: {
		defaultValue: 'Exam is required',
		description: 'Error message for exam required',
		displayPath: `${pathStart}Error | Exam Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPSettingsScoreReportsBatchButtonFindScoreReports: {
		defaultValue: 'Find Score Reports',
		description: 'Button for finding score reports',
		displayPath: `${pathStart}Button | Find Score Reports`,
		displayType: 'Button',
		...sharedProperties,
	},
};

export type SPSettingsScoreReportsBatchLocType = LocMap<SPSettingsScoreReportsBatchLocNames>;
